import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../axios/axiosInstance";
import { showToast } from "./ToastSlice";

export const fetchDIDSettings = createAsyncThunk(
  "didlogicAdmin/fetchSettings",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/superAdmin/didlogic/settings");
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch DID settings");
    }
  }
);

export const saveDIDSettings = createAsyncThunk(
  "didlogicAdmin/saveSettings",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.put("/superAdmin/didlogic/settings", payload);
      dispatch(showToast({ message: "DIDLogic settings saved.", variant: "success" }));
      return res.data;
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || "Failed to save settings", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const syncDIDNumbers = createAsyncThunk(
  "didlogicAdmin/syncNumbers",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.post("/superAdmin/didlogic/numbers/sync");
      dispatch(showToast({ message: res.data.message || "Numbers synced.", variant: "success" }));
      return res.data;
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || "Sync failed", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const fetchAdminDIDNumbers = createAsyncThunk(
  "didlogicAdmin/fetchNumbers",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/superAdmin/didlogic/numbers", { params });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch numbers");
    }
  }
);

export const fetchDIDBalance = createAsyncThunk(
  "didlogicAdmin/fetchBalance",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/superAdmin/didlogic/balance");
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch balance");
    }
  }
);

export const fetchAdminCallRecords = createAsyncThunk(
  "didlogicAdmin/fetchCallRecords",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/superAdmin/didlogic/call-records", { params });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch call records");
    }
  }
);

const didlogicAdminSlice = createSlice({
  name: "didlogicAdmin",
  initialState: {
    settings: null,
    loading: false,
    saving: false,
    syncing: false,

    balance: null,
    balanceLoading: false,
    balanceError: null,
    balanceLastFetched: null,

    allNumbers: [],
    allNumbersTotal: 0,
    numbersLoading: false,

    callRecords: [],
    callPagination: {},
    callLoading: false,

    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDIDSettings.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(fetchDIDSettings.fulfilled, (s, a) => { s.loading = false; s.settings = a.payload.data; })
      .addCase(fetchDIDSettings.rejected, (s, a) => { s.loading = false; s.error = a.payload; })

      .addCase(saveDIDSettings.pending, (s) => { s.saving = true; })
      .addCase(saveDIDSettings.fulfilled, (s, a) => { s.saving = false; s.settings = a.payload.data; })
      .addCase(saveDIDSettings.rejected, (s) => { s.saving = false; })

      .addCase(syncDIDNumbers.pending, (s) => { s.syncing = true; })
      .addCase(syncDIDNumbers.fulfilled, (s) => { s.syncing = false; })
      .addCase(syncDIDNumbers.rejected, (s) => { s.syncing = false; })

      .addCase(fetchDIDBalance.pending,   (s) => { s.balanceLoading = true; s.balanceError = null; })
      .addCase(fetchDIDBalance.fulfilled, (s, a) => {
        s.balanceLoading = false;
        s.balance = a.payload.data?.balance ?? null;
        s.balanceLastFetched = new Date().toISOString();
      })
      .addCase(fetchDIDBalance.rejected,  (s, a) => { s.balanceLoading = false; s.balanceError = a.payload; })

      .addCase(fetchAdminDIDNumbers.pending, (s) => { s.numbersLoading = true; })
      .addCase(fetchAdminDIDNumbers.fulfilled, (s, a) => { s.numbersLoading = false; s.allNumbers = a.payload.data || []; s.allNumbersTotal = a.payload.total || 0; })
      .addCase(fetchAdminDIDNumbers.rejected, (s) => { s.numbersLoading = false; })

      .addCase(fetchAdminCallRecords.pending, (s) => { s.callLoading = true; })
      .addCase(fetchAdminCallRecords.fulfilled, (s, a) => { s.callLoading = false; s.callRecords = a.payload.data || []; s.callPagination = a.payload.pagination || {}; })
      .addCase(fetchAdminCallRecords.rejected, (s) => { s.callLoading = false; });
  },
});

export default didlogicAdminSlice.reducer;
